/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

/* eslint-disable no-nested-ternary */
import { Button, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import PlaygroundPlayIcon from '@/components/icons/PlaygroundPlayIcon';
import PlaygroundPauseIcon from '@/components/icons/PlaygroundPauseIcon';
import { useRunCtxV2 } from '@/provider/runv2';
import {
  EditOutlined,
  MessageOutlined,
  WarningOutlined,
  RedoOutlined
} from '@ant-design/icons';
import { useReactiveVar, useSubscription } from '@apollo/client';
import { ON_WORKER_STATE_UPDATE } from '@/graphql/subscriptions/playground';
import NewProcedureFromRun from '@/components/NewProcedureFromRun/NewProcedureFromRun';
import RunDebugDetails from '@/components/RunDebugDetails';
import classnames from 'classnames';
import {
  deleteCurrentTemplateId,
  deleteTemplateIdForRequest,
  getTemplateIdForWorker,
  removeTemplateIdForWorker,
  setUsedTemplates
} from '@/pages/Playgrounds/templateUtils';
import AccessControl from '@/components/AccessControl/AccessControl';
import { ProcessPermissionsEnum } from '@/utils/permissionConstants';
import { currentUserDetailsVar } from '@/graphql/cache/user';
import { useSelector } from 'react-redux';
import { trackEvent } from '@/tracker';
import { AnalyticsEvents } from '@/analytics/events';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useOutletContext } from 'react-router-dom';
import { IProcedureOutletContext } from '@/pages/RunViewLayout';
import useALC from '@/hooks/useALCFlag';
import ReplayRun from '@/components/ReplayRun/ReplayRun';
import { useKogEditorContext } from '../Editor/EditorProvider';
import styles from './EditorHeader.module.less';
import { EditorSourceType } from '../Editor/editorInterface';
import { isMiniPlaygroundOpen } from '../../../../utils/playground';
import { popupSelector } from '../../../../stores/slices/appPopup';
import { textDeFormatter } from '../EditorWidgets/decorators';
import RetryRun from '../../../../components/RetryRun/RetryRun';

interface EditorHeaderProps {
  workerId: string;
  isKonciergeVisible: boolean;
}

enum WorkerState {
  CREATING = 'creating',
  CREATED = 'created',
  PROCESSING = 'processing',
  USER_STOPPED = 'user stopped',
  WAITING_FOR_USER_INPUT = 'waiting_for_user_input',
  DONE = 'done',
  DELETED = 'deleted',
  ERROR = 'error',
  STARTED = 'started',
  PREAPRING_KNOWLEDGE = 'preparing_knowledge',
  KNOWLEDGE_READY = 'ready'
}

const mood: any = {
  Brain: '😐',
  'Execution started': '😐',
  'Preparing Worker': '🤓',
  'Worker ready': '🤓',
  Preparing: '🤓',
  Processing: '🤔',
  Paused: '😎',
  Done: '🤩',
  Failed: '😭'
};

function EditorHeader({ workerId, isKonciergeVisible }: EditorHeaderProps) {
  const { worker } = useRunCtxV2();
  const outletContext = useOutletContext<IProcedureOutletContext>();
  const {
    source,
    debugMode,
    editorContent,
    editorStatus,
    actions,
    replacerObj
  } = useKogEditorContext();
  const { currentActivePopups } = useSelector(popupSelector);
  const [{ rolloutRestartPauseWorker }] = useFeatureFlag();

  const { isALCEnabled } = useALC();

  const isMiniPlayground = source === EditorSourceType.MINI_PLAYGROUND;
  const isRunPage = source === EditorSourceType.PROCESS_RUN;

  const { email } = useReactiveVar(currentUserDetailsVar);

  const [status, setStatus] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string>('Processing');
  // const statusText = ['Idle', 'Processing', 'Paused', 'Completed', 'Failed'];

  useEffect(() => {
    switch (status) {
      case WorkerState.CREATED:
      case WorkerState.CREATING:
        setStatusText('Brain');
        break;
      case WorkerState.PREAPRING_KNOWLEDGE:
        setStatusText('Preparing Worker');
        break;
      case WorkerState.KNOWLEDGE_READY:
        setStatusText('Worker ready');
        break;
      case WorkerState.DONE:
        // This is done for self-service templates
        setUsedTemplates(getTemplateIdForWorker(worker.id, email!), email!);
        deleteCurrentTemplateId(email!);
        removeTemplateIdForWorker(worker.id, email!);
        deleteTemplateIdForRequest(email!);
        deleteCurrentTemplateId(email!);
        setStatusText('Done');
        break;
      case WorkerState.PROCESSING:
        setStatusText('Processing');
        break;
      case WorkerState.ERROR:
        setStatusText('Failed');
        break;
      case WorkerState.WAITING_FOR_USER_INPUT:
        setStatusText('Paused');
        break;
      case WorkerState.STARTED:
      default:
        setStatusText('Execution started');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (worker?.state) {
      setStatus(worker.state);
    }
  }, [worker?.state]);

  useEffect(() => {
    if (editorStatus.isExecuting) {
      setStatus(WorkerState.STARTED);
    }
  }, [editorStatus.isExecuting]);

  const { error: subscriptionError } = useSubscription(ON_WORKER_STATE_UPDATE, {
    variables: { id: worker?.id },
    skip: !worker || isMiniPlaygroundOpen(source, currentActivePopups),
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.onNotifyWorkerUpdate?.state) {
        setStatus(subscriptionData.data.onNotifyWorkerUpdate.state);
      }
    }
  });

  useEffect(() => {
    if (subscriptionError) {
      console.log(subscriptionError);
    }
  }, [subscriptionError]);

  const pauseHandler = () => {
    // TODO: Implement Pause Worker when Pause feature is enabled in the backend
    // if (editorStatus.readOnly) {
    //   return;
    // }
    // pauseWorker({
    //   variables: {
    //     workerId
    //   },
    //   onCompleted: () => {
    //     actions.updateEditorStatus({ isExecuting: false, isEditing: true });
    //   }
    // });
  };

  return (
    <div className={styles.editorHeader}>
      <div className={styles.editorProgress}>
        {worker?.id ? (
          <>
            {/* TODO: Remove once KOG-6748 is resolved. */}
            <div
              data-cy={classnames({
                'new-worker-ready-to-use': [
                  WorkerState.CREATED,
                  WorkerState.KNOWLEDGE_READY
                ].includes(status as WorkerState)
              })}
            />
            <RunDebugDetails
              worker={worker}
              workerId={worker.id}
              showCreatedAt
              isDebugMode={debugMode}
              handleDebugMode={actions.toggleDebugMode}
            >
              <div
                style={{ fontSize: isMiniPlayground ? 24 : 32, lineHeight: 1 }}
              >
                {mood[statusText]}
              </div>
            </RunDebugDetails>
            <Typography.Text
              style={{
                fontSize: 12,
                lineHeight: '32px',
                marginLeft: 10,
                color: '#748f9e'
              }}
              data-cy="worker-status-text"
            >
              {statusText}
            </Typography.Text>
            {statusText === 'Processing' ||
            statusText === 'Preparing Worker' ? (
              <div className="loading-container">
                <div className={styles.loader} data-cy="worker-status-loader" />
              </div>
            ) : null}
          </>
        ) : (
          <div />
        )}
      </div>
      <div className={styles.editorButtons}>
        {/* <Switch
          checkedChildren="Edit Mode"
          unCheckedChildren="Play Mode"
          onChange={onModeChange}
          checked={editMode}
        /> */}
        {!isMiniPlayground && (
          <Typography.Text type="secondary" data-cy="document-status">
            {editorStatus.isExecuting
              ? 'Sent for execution'
              : editorStatus.isSaving
              ? 'Saving...'
              : 'Saved'}
          </Typography.Text>
        )}

        {!editorStatus.isExecuting ? (
          <Tooltip
            title={
              editorStatus.executionEnabled ? (
                'Run'
              ) : (
                <div data-cy="document-run-button-tooltip">
                  <WarningOutlined /> Parse Error;
                  <br />
                  <span style={{ fontSize: 10 }}>
                    Scrolling may be required to see all issues.
                  </span>
                </div>
              )
            }
          >
            <Button
              type="text"
              shape="default"
              className="display-flex align-center justify-center"
              onClick={() =>
                actions.onPlay(workerId, {
                  shouldStartExecution: true,
                  defaultRun: true
                })
              }
              icon={<PlaygroundPlayIcon />}
              disabled={!editorStatus.executionEnabled}
              data-cy={
                isMiniPlayground
                  ? 'mp-document-run-button'
                  : 'document-run-button'
              }
              size={isMiniPlayground ? 'small' : undefined}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Pause">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: !editorStatus.readOnly
                }
              )}
              onClick={pauseHandler}
              icon={<PlaygroundPauseIcon />}
              size={isMiniPlayground ? 'small' : undefined}
              data-cy="document-pause-button"
            />
          </Tooltip>
        )}

        {!isMiniPlayground && !isRunPage && rolloutRestartPauseWorker && (
          <Tooltip title="Restart">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex',
                'align-center',
                'justify-center',
                styles.restartButton
              )}
              data-cy="document-restart-button"
              onClick={() => {
                trackEvent(AnalyticsEvents.PLAYGROUND.PGRestartButton, {
                  playgroundId: workerId
                });
                actions.onRestart(workerId);
              }}
              icon={<RedoOutlined />}
            />
          </Tooltip>
        )}

        {!isMiniPlayground && isRunPage ? (
          <Tooltip title="Edit Run">
            <Button
              type="text"
              shape="default"
              data-cy="document-edit-button"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: !editorStatus.readOnly
                }
              )}
              onClick={() => {
                actions.updateEditorStatus({
                  readOnly: !editorStatus.readOnly
                });
              }}
              icon={
                <EditOutlined style={{ color: '#748F9E', fontSize: '18px' }} />
              }
            />
          </Tooltip>
        ) : null}

        {!isALCEnabled && !isMiniPlayground ? (
          <AccessControl
            requiredPermissions={[
              ProcessPermissionsEnum.ProcessRead,
              ProcessPermissionsEnum.ProcessCreate,
              ProcessPermissionsEnum.ProcessUpdate
            ]}
            RestrictedPage={null}
          >
            <NewProcedureFromRun
              editorContent={textDeFormatter(editorContent, replacerObj)}
              workerId={worker?.id}
              procedure={outletContext?.procedure}
              isRunPage={isRunPage}
            />
          </AccessControl>
        ) : null}

        {/* TODO: Move to a different comp */}
        {worker?.id && !isMiniPlayground ? (
          <RetryRun
            workerId={worker.id}
            isPlayground={!outletContext?.procedure}
          />
        ) : null}

        <ReplayRun
          worker={worker}
          isRunPage={isRunPage}
          isMiniPlayground={isMiniPlayground}
        />

        {!editorStatus.readOnly && !isMiniPlayground ? (
          <Tooltip title="Koncierge">
            <Button
              type="text"
              shape="default"
              data-cy="koncierge-button"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: isKonciergeVisible
                }
              )}
              onClick={() => {
                trackEvent(
                  editorStatus.showChat
                    ? AnalyticsEvents.PLAYGROUND.PGKonciergeButtonClose
                    : AnalyticsEvents.PLAYGROUND.PGKonciergeButtonOpen,
                  {
                    playgroundId: workerId
                  }
                );
                const showChat = !editorStatus.showChat;
                actions.updateEditorStatus({
                  showChat,
                  showOutputPanel: !showChat
                });
              }}
              icon={
                <MessageOutlined style={{ height: '20px', width: '20px' }} />
              }
            />
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}

export default EditorHeader;
